/* eslint-disable react/no-array-index-key */
import { Avatar } from '@lmc/ui-kit'
import styled from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import EventTitlePopover from '../popovers/EventTitlePopover'
import EventIcon from '../EventIcon'
import AllocationPopover from '../popovers/AllocationPopover'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import AllocationWindow from '../../../screens/AllocationWindow/AllocationWindow'
import {
  getDateTimeInZone,
  TIME_FORMAT,
} from 'screens/Rostering/utils.datetime'
import { Home, User } from '@lmc/ts-common'
import { RESIDENT } from 'screens/Rostering/constants'
import useFeatureFlags from 'hooks/useFeatureFlags'
import WarningPopover from '../popovers/WarningPopover'
import { WarningIcon } from '../icons'
import dayjs from 'dayjs'
import AllocateIcon from '../icons/AllocateIcon'
import { convertToMinutes } from './utils'
import { RotaInstanceStatus } from 'screens/Rostering/types'
import { useSelector } from 'react-redux'
import { getNominative } from '@lmc/common/utils'
import QuickAllocationPopover from '../popovers/QuickAllocationPopover'

type EventProps = {
  args: {
    original: {
      id: string
      teamMembers: { carer: User; teamMemberId: string }[]
      expectedDuration: string
      startTime: string
      endTime: string
      status: RotaInstanceStatus
      modified: boolean
      title: string
      flexiType: boolean
      instanceType: string
      numberSupply: number
      resident: User
      deletedServiceUser: string | undefined
    }
    start: string
    end: string
    color: string
  }
  type?: 'user' | 'shift' | 'resident'
  warnings: string[]
  setDrawerVisible: (flag: boolean) => void
  setDrawerAllocatedTeamVisible: (flag: boolean) => void
  isDailyView: boolean
}

const Event = ({
  args,
  type = RESIDENT,
  warnings,
  setDrawerVisible,
  setDrawerAllocatedTeamVisible,
  isDailyView,
}: EventProps) => {
  const { t } = useTranslation()

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative,
  )

  const residentNominative = getNominative(
    preferredPeopleNominative,
    'Resident',
  )

  const {
    original: {
      teamMembers,
      expectedDuration,
      startTime,
      endTime,
      status,
      modified,
      title,
      flexiType,
      // instanceType,
      numberSupply,
      resident,
      deletedServiceUser,
    },
    start,
    end,
    color,
  } = args

  const flags = useFeatureFlags({
    flagNames: ['rostering_parked_features', 'rostering_quick_allocation'],
  })?.data

  const flagsRosteringParkedFeaturesEnabled =
    flags?.rostering_parked_features?.enabled

  const flagsRosteringQuickAllocationEnabled =
    flags?.rostering_quick_allocation?.enabled

  if (!args?.original) return null

  const [allocationWindowVisible, setAllocationWindowVisible] = useState(false)

  const [allocationPopoverOpen, setAllocationPopoverOpen] = useState(false)
  const [quickAllocationPopoverOpen, setQuickAllocationPopoverOpen] =
    useState(false)

  const formatTime = (time: string) => {
    if (time.includes('/')) {
      return dayjs(time).format('HH:mm')
    }
    return dayjs(time, TIME_FORMAT).format('HH:mm')
  }

  const timeInterval = `${formatTime(start)}-${formatTime(end)}`

  const isOvernight =
    getDateTimeInZone(startTime).date() < getDateTimeInZone(endTime).date()

  const handleIconClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()

    if (
      deletedServiceUser ||
      status === RotaInstanceStatus.CANCELLED ||
      status === RotaInstanceStatus.DONE
    ) {
      setDrawerAllocatedTeamVisible(true)
    } else if (flagsRosteringQuickAllocationEnabled) {
      setQuickAllocationPopoverOpen(true)
      setAllocationPopoverOpen(false)
    } else {
      setAllocationWindowVisible(true)
    }
  }

  const allocatedCount = teamMembers?.length || 0
  const missingAllocationsNumber = Math.max(numberSupply - allocatedCount, 0)

  let residentName = ''

  if (resident?.name?.first && resident?.name?.last) {
    residentName = `${resident.name.first} ${resident.name.last}`
  } else if (deletedServiceUser) {
    residentName = t('DELETED_NOMINATIVE', { nominative: residentNominative })
  } else {
    residentName = title
  }

  const isDraft = status === RotaInstanceStatus.DRAFT
  const showWarnings = !isDraft && warnings.length > 0
  const isModified = modified && !isDraft
  const expectedDurationInMinutes = convertToMinutes(expectedDuration)

  return (
    <>
      <Container
        $color={color}
        $showWarnings={showWarnings}
        onClick={() => setDrawerVisible(true)}
        data-cy="rota-event-container"
      >
        {isModified && <Blob />}
        <LeftContainer>
          <Row>
            <IconTitleContainer>
              {showWarnings ? (
                <WarningPopover warningsData={warnings}>
                  <WarningIconWrapper>
                    <WarningIcon />
                  </WarningIconWrapper>
                </WarningPopover>
              ) : (
                status !== RotaInstanceStatus.PENDING && (
                  <EventIcon status={status} />
                )
              )}
              <EventTitlePopover
                original={args.original}
                hoursText={expectedDuration}
                timeInterval={timeInterval}
              >
                <Title
                  level={1.5}
                  weight="bold"
                  $strikethrough={status === RotaInstanceStatus.CANCELLED}
                  data-cy="event-title"
                >
                  {type === RESIDENT ? title : residentName}
                </Title>
              </EventTitlePopover>
            </IconTitleContainer>
          </Row>
          <Details>
            <HoursSection>
              <DetailText level={2} data-cy="event-time-interval">
                {timeInterval}
              </DetailText>
              {isOvernight && (
                <OvernightText level={2}>{t('PLUS_ONE')}</OvernightText>
              )}
            </HoursSection>
            <Separator level={2}>·</Separator>
            {flexiType && flagsRosteringParkedFeaturesEnabled && (
              <>
                <Separator level={2}>·</Separator>
                <DetailText level={2}>{t('FLEXI')}</DetailText>
              </>
            )}
          </Details>
        </LeftContainer>
        {(!isDailyView || expectedDurationInMinutes > 30) && (
          <RightContainer>
            {/* <TertiaryText level={2}>{instanceType}</TertiaryText> */}
            <PopoversContainer>
              <>
                {quickAllocationPopoverOpen && (
                  <QuickAllocationPopover
                    isOpen={quickAllocationPopoverOpen}
                    setIsOpen={setQuickAllocationPopoverOpen}
                    setAllocationWindowVisible={setAllocationWindowVisible}
                    revisionId={args.original.id}
                  >
                    <AllocationPopover
                      missingAllocationsNumber={missingAllocationsNumber}
                      hasAllocatedTeamMembers={allocatedCount > 0}
                      original={args.original}
                      args={args}
                      setAllocationWindowVisible={
                        deletedServiceUser
                          ? () => {}
                          : setAllocationWindowVisible
                      }
                      setDrawerVisible={setDrawerAllocatedTeamVisible}
                      isOpen={allocationPopoverOpen}
                      setIsOpen={setAllocationPopoverOpen}
                      disabled={quickAllocationPopoverOpen}
                    >
                      <AllocationsContainer onClick={handleIconClick}>
                        <AllocationActions>
                          {numberSupply === 1 && allocatedCount === 1 && (
                            <StyledAvatar
                              size={24}
                              url={
                                teamMembers[0]?.carer
                                  ?.picture as unknown as string
                              }
                            />
                          )}
                          {numberSupply === 1 && allocatedCount !== 1 && (
                            <AllocateIconContainer data-cy="allocate-icon">
                              <AllocateIcon />
                            </AllocateIconContainer>
                          )}
                          {numberSupply > 1 && (
                            <>
                              {missingAllocationsNumber > 0 ? (
                                <AllocateIconContainer data-cy="allocate-icon">
                                  <AllocateIcon />
                                </AllocateIconContainer>
                              ) : (
                                <StyledAvatar
                                  size={24}
                                  url={
                                    teamMembers[0]?.carer
                                      ?.picture as unknown as string
                                  }
                                />
                              )}
                              <PlusText level={2}>+{numberSupply - 1}</PlusText>
                            </>
                          )}
                        </AllocationActions>
                      </AllocationsContainer>
                    </AllocationPopover>
                  </QuickAllocationPopover>
                )}
                {!quickAllocationPopoverOpen && (
                  <AllocationPopover
                    missingAllocationsNumber={missingAllocationsNumber}
                    hasAllocatedTeamMembers={allocatedCount > 0}
                    original={args.original}
                    args={args}
                    setAllocationWindowVisible={
                      deletedServiceUser ? () => {} : setAllocationWindowVisible
                    }
                    setDrawerVisible={setDrawerAllocatedTeamVisible}
                    isOpen={allocationPopoverOpen}
                    setIsOpen={setAllocationPopoverOpen}
                    disabled={quickAllocationPopoverOpen}
                  >
                    <AllocationsContainer onClick={handleIconClick}>
                      <AllocationActions>
                        {numberSupply === 1 && allocatedCount === 1 && (
                          <StyledAvatar
                            size={24}
                            url={
                              teamMembers[0]?.carer
                                ?.picture as unknown as string
                            }
                          />
                        )}
                        {numberSupply === 1 && allocatedCount !== 1 && (
                          <AllocateIconContainer data-cy="allocate-icon">
                            <AllocateIcon />
                          </AllocateIconContainer>
                        )}
                        {numberSupply > 1 && (
                          <>
                            {missingAllocationsNumber > 0 ? (
                              <AllocateIconContainer data-cy="allocate-icon">
                                <AllocateIcon />
                              </AllocateIconContainer>
                            ) : (
                              <StyledAvatar
                                size={24}
                                url={
                                  teamMembers[0]?.carer
                                    ?.picture as unknown as string
                                }
                              />
                            )}
                            <PlusText level={2}>+{numberSupply - 1}</PlusText>
                          </>
                        )}
                      </AllocationActions>
                    </AllocationsContainer>
                  </AllocationPopover>
                )}
              </>
            </PopoversContainer>
          </RightContainer>
        )}
      </Container>
      {allocationWindowVisible && (
        <AllocationWindow
          allocationWindowVisible={allocationWindowVisible}
          onCancel={() => setAllocationWindowVisible(false)}
          revisionDetail={args.original}
        />
      )}
    </>
  )
}

const StyledAvatar = styled(Avatar)`
  border: 0.5px solid ${(props) => props.theme.color.neutral.color7};
`

const PopoversContainer = styled.div`
  display: flex;
  align-items: center;
`

const AllocateIconContainer = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${(props) => props.theme.color.neutral.color7};
  border-radius: 50%;
  width: 24px;
  height: 24px;
  justify-content: center;
  cursor: pointer;
`

const LeftContainer = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
`

const RightContainer = styled.div``

const WarningIconWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`

const PlusText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color4};
  margin-left: 2px;
`

const AllocationsContainer = styled.div`
  display: flex;
  align-items: center;
  height: 16px;
  position: relative;

  &:hover {
    ${StyledAvatar},${AllocateIconContainer}, &:hover ${PlusText} {
      border-color: ${({ theme }) => theme.color.hover.primary};
      color: ${({ theme }) => theme.color.hover.primary};
    }
  }
`

const Container = styled.div<{ $color: string; $showWarnings: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  width: 100%;
  background: ${(props) => (props.$showWarnings ? '#FFF7F0' : 'white')};
  padding: 4px 4px 4px 4px;
  border: 1px solid ${(props) => props.theme.color.neutral.color7};
  border-radius: 4px;
  border-left: 5px solid ${(props) => props.$color};
  position: relative;

  &:hover {
    border: 1px solid ${(props) => props.theme.color.neutral.color6};
    border-left: 5px solid ${(props) => props.$color};
    box-shadow: 0px 0px 8px 0px rgba(34, 34, 34, 0.2);
  }
`

const Title = styled(Text)<{ $strikethrough: boolean }>`
  color: ${(props) => props.theme.color.neutral.color3};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
  text-decoration: ${({ $strikethrough }) =>
    $strikethrough ? 'line-through' : undefined};
`

const OvernightText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
  position: relative;
  top: 0;
  transform: translateY(-10%);
`

const HoursSection = styled.div`
  display: flex;
  gap: 1px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
`

const DetailText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color3};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
`

const Blob = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  height: 10px;
  width: 10px;
  border-radius: 0 3px 0 4px;
  background: ${(props) => props.theme.color.status.popout};
  border-left: 0.5px solid ${(props) => props.theme.color.neutral.color9};
  border-bottom: 0.5px solid ${(props) => props.theme.color.neutral.color9};
`

const Details = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`

const IconTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`

const AllocationActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Separator = styled(DetailText)`
  color: ${(props) => props.theme.color.neutral.color5};
`

export default Event
